import { useState, FormEvent } from 'react';
import type { LocationData } from '../locationData';

interface Props {
  location: LocationData;
}

export default function LocationContact({ location }: Props) {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [charCount, setCharCount] = useState(0);

  // City coordinates for map centering (approximate city centers)
  const cityCoordinates: Record<string, { lat: number; lng: number; zoom: number }> = {
    newmarket: { lat: 44.0592, lng: -79.4613, zoom: 13 },
    aurora: { lat: 44.0065, lng: -79.4504, zoom: 13 },
    stouffville: { lat: 43.9667, lng: -79.2500, zoom: 13 },
    'richmond-hill': { lat: 43.8828, lng: -79.4403, zoom: 13 },
    thornhill: { lat: 43.8167, lng: -79.4500, zoom: 13 },
    markham: { lat: 43.8561, lng: -79.3370, zoom: 12 },
    scarborough: { lat: 43.7764, lng: -79.2318, zoom: 12 },
    pickering: { lat: 43.8384, lng: -79.0868, zoom: 13 },
    ajax: { lat: 43.8509, lng: -79.0204, zoom: 13 },
    whitby: { lat: 43.8975, lng: -78.9429, zoom: 13 },
    oshawa: { lat: 43.8971, lng: -78.8658, zoom: 12 },
  };

  const coords = cityCoordinates[location.slug] || { lat: 43.7, lng: -79.4, zoom: 10 };
  const mapEmbedUrl = `https://www.google.com/maps/embed?pb=!1m14!1m12!1m3!1d50000!2d${coords.lng}!3d${coords.lat}!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!5e0!3m2!1sen!2sca!4v1`;

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);

    const message = data.get('message') as string;
    if (message && message.length > 500) return;

    setSubmitting(true);
    const body = new URLSearchParams();
    data.forEach((value, key) => {
      body.append(key, value.toString());
    });

    try {
      await fetch('https://formsubmit.readdy.ai/api/submit/location-quote-mowandsnow', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="loc-contact" className="py-20 bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Map Section */}
        <div className="mb-12">
          <div className="bg-white rounded-2xl overflow-hidden border border-gray-100 shadow-sm">
            <div className="p-4 border-b border-gray-100 flex items-center gap-3">
              <div className="w-10 h-10 flex items-center justify-center bg-green-100 rounded-full">
                <i className="ri-map-2-line text-green-600 text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-gray-900">Service Area Map</h3>
                <p className="text-sm text-gray-500">We operate throughout {location.name} and surrounding neighbourhoods</p>
              </div>
            </div>
            <div className="relative h-64 sm:h-80 bg-gray-100">
              <iframe
                src={mapEmbedUrl}
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title={`${location.name} service area map`}
                className="absolute inset-0"
              />
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-start">
          {/* Left Info */}
          <div>
            <div className="inline-block px-4 py-2 bg-yellow-50 text-yellow-700 rounded-full text-sm font-semibold mb-4">
              Get a Free Quote
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Ready to transform your {location.name} property?
            </h2>
            <p className="text-lg text-gray-600 mb-8">
              {location.longDescription}
            </p>

            <div className="space-y-4">
              <a href="tel:+16479837924" className="flex items-center space-x-4 group cursor-pointer">
                <div className="w-12 h-12 flex items-center justify-center bg-yellow-100 rounded-xl group-hover:bg-yellow-200 transition-colors">
                  <i className="ri-phone-line text-xl text-yellow-700"></i>
                </div>
                <div>
                  <p className="text-xs text-gray-500 font-medium">Call Us</p>
                  <p className="text-gray-900 font-semibold group-hover:text-yellow-600 transition-colors">(647) 983-7924</p>
                </div>
              </a>
              <a href="mailto:info@mowandsnow.co" className="flex items-center space-x-4 group cursor-pointer">
                <div className="w-12 h-12 flex items-center justify-center bg-green-100 rounded-xl group-hover:bg-green-200 transition-colors">
                  <i className="ri-mail-line text-xl text-green-700"></i>
                </div>
                <div>
                  <p className="text-xs text-gray-500 font-medium">Email Us</p>
                  <p className="text-gray-900 font-semibold group-hover:text-green-600 transition-colors">info@mowandsnow.co</p>
                </div>
              </a>
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 flex items-center justify-center bg-cyan-100 rounded-xl">
                  <i className="ri-map-pin-line text-xl text-cyan-700"></i>
                </div>
                <div>
                  <p className="text-xs text-gray-500 font-medium">Service Area</p>
                  <p className="text-gray-900 font-semibold">{location.name} &amp; surrounding GTA</p>
                </div>
              </div>
            </div>
          </div>

          {/* Right Form */}
          <div className="bg-white rounded-2xl p-8 border border-gray-100">
            {submitted ? (
              <div className="text-center py-10">
                <div className="w-16 h-16 flex items-center justify-center bg-green-100 rounded-full mx-auto mb-4">
                  <i className="ri-check-line text-3xl text-green-600"></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Quote Request Sent!</h3>
                <p className="text-gray-600">
                  Thanks! We&apos;ll be in touch shortly to discuss your {location.name} property.
                </p>
              </div>
            ) : (
              <form data-readdy-form onSubmit={handleSubmit} className="space-y-5">
                <input type="hidden" name="city" value={location.name} />
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">First Name</label>
                    <input
                      type="text"
                      name="first_name"
                      required
                      placeholder="John"
                      className="w-full px-4 py-3 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-green-400 transition"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Last Name</label>
                    <input
                      type="text"
                      name="last_name"
                      required
                      placeholder="Smith"
                      className="w-full px-4 py-3 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-green-400 transition"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Email</label>
                  <input
                    type="email"
                    name="email"
                    required
                    placeholder="john@example.com"
                    className="w-full px-4 py-3 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-green-400 transition"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Phone</label>
                  <input
                    type="tel"
                    name="phone"
                    placeholder="(416) 555-0100"
                    className="w-full px-4 py-3 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-green-400 transition"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Service Interested In</label>
                  <select
                    name="service"
                    required
                    className="w-full px-4 py-3 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-green-400 transition bg-white"
                  >
                    <option value="">Select a service...</option>
                    <option value="Lawn Mowing">Lawn Mowing</option>
                    <option value="Gardening">Gardening</option>
                    <option value="Yard Cleanup">Yard Cleanup</option>
                    <option value="Snow Removal">Snow Removal</option>
                    <option value="Driveway Hardscaping">Driveway Hardscaping</option>
                    <option value="Patio Hardscaping">Patio Hardscaping</option>
                    <option value="All-Season Plan">All-Season Plan</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">
                    Message <span className="text-gray-400 font-normal">({charCount}/500)</span>
                  </label>
                  <textarea
                    name="message"
                    rows={3}
                    maxLength={500}
                    placeholder={`Tell us about your ${location.name} property...`}
                    onChange={(e) => setCharCount(e.target.value.length)}
                    className="w-full px-4 py-3 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-green-400 transition resize-none"
                  ></textarea>
                </div>
                <button
                  type="submit"
                  disabled={submitting || charCount > 500}
                  className="w-full py-4 bg-gradient-to-r from-yellow-400 to-yellow-500 text-gray-900 font-bold rounded-lg hover:from-yellow-500 hover:to-yellow-600 transition-all whitespace-nowrap cursor-pointer disabled:opacity-60"
                >
                  {submitting ? 'Sending...' : 'Request Free Quote'}
                </button>
                <p className="text-xs text-gray-400 text-center">
                  We respond within 24 hours. No spam, ever.
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
